import React, { useState, useEffect, useCallback, ReactNode } from 'react';
import { useRouter } from 'next/router';
import { Box, Typography } from '@mui/material';
import {
  useAuth,
  LMA_ACTIVE_SESSION_EXPIRES_AT_KEY,
  LMA_IDLE_LOGOUT_SESSION_KEY,
  LMA_USER_REGISTERED_SESSION_KEY,
  LMA_USERNAME_LOCAL_KEY,
  LMA_SESSION_ENDED_BY_ADMIN_PATH_SEGMENT,
} from 'contexts/AuthContext';
import { useIdleTimerHook } from 'hooks/useIdleTimer';
import { clearUserLocks, unregisterActiveUser } from 'src/services/leadManagementApi';
import { IdleTimeoutDialog } from 'components/LMA/IdleTimeoutDialog/IdleTimeoutDialog';
import { LMAUnauthorizedView, type AccessDeniedFields } from './LMAUnauthorizedView';
import { LMAConcurrentSessionGate } from './LMAConcurrentSessionGate';

export type { AccessDeniedFields };

interface AuthorizationProps {
  children: ReactNode;
  accessDeniedFields?: AccessDeniedFields;
  /** Sitecore LMA site settings (layout context) — used for idle timeout config and concurrent session gate */
  lmaSiteSettings?: Record<string, any>;
}

export function Authorization({
  children,
  accessDeniedFields,
  lmaSiteSettings,
}: AuthorizationProps) {
  const { isAuthenticated, isLoading, userName, userRole } = useAuth();
  const router = useRouter();
  const [mounted, setMounted] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState<number | undefined>(undefined);

  const idleTimeoutMs = (lmaSiteSettings?.AutoLogoutTimeoutSeconds?.value ?? 1200) * 1000;
  const idlePromptTimeoutMs =
    (lmaSiteSettings?.AutoLogoutPromptTimeoutSeconds?.value ?? 600) * 1000;
  const logoutRedirectUrl = lmaSiteSettings?.AutoLogoutButton?.value?.href || '/logout';

  const maxConcurrentUsers = Number(lmaSiteSettings?.MaxConcurrentUsers?.value) || 0;
  const userSessionTtlSeconds = Number(lmaSiteSettings?.UserSessionTTLSeconds?.value) || 3600;
  const isSuperAdmin = userRole === 'LMASuperAdmin';

  /** Terminal routes should not run gate/idle logic. */
  const pathSegments = router.asPath
    .split('?')[0]
    .split('/')
    .filter(Boolean)
    .map((seg) => seg.toLowerCase().replace(/[^a-z0-9]/g, ''));
  const sessionEndedSegment = LMA_SESSION_ENDED_BY_ADMIN_PATH_SEGMENT.toLowerCase().replace(
    /[^a-z0-9]/g,
    ''
  );
  const isSessionEndedPage = pathSegments.includes(sessionEndedSegment);
  const isLogoutPage = pathSegments.includes('logout');
  const isTerminalLmaPage = isSessionEndedPage || isLogoutPage;

  const shouldEnableIdleTimer = mounted && isAuthenticated && !isTerminalLmaPage;

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (shouldEnableIdleTimer) {
      console.log('Idle timer enabled:', {
        idleTimeoutSeconds: idleTimeoutMs / 1000,
        promptTimeoutSeconds: idlePromptTimeoutMs / 1000,
        logoutRedirectUrl: logoutRedirectUrl,
      });
    }
  }, [shouldEnableIdleTimer, idleTimeoutMs, idlePromptTimeoutMs, logoutRedirectUrl]);

  const onIdleTimeout = useCallback(() => {
    sessionStorage.setItem(LMA_IDLE_LOGOUT_SESSION_KEY, 'true');
    const u =
      (typeof window !== 'undefined' && localStorage.getItem(LMA_USERNAME_LOCAL_KEY)) ||
      userName ||
      '';
    if (u) {
      unregisterActiveUser(u).catch(() => undefined);
      if (process.env.NEXT_PUBLIC_LMA_LOCK_ENABLED === 'true') {
        clearUserLocks(u).catch(() => undefined);
      }
    }
    sessionStorage.removeItem(LMA_USER_REGISTERED_SESSION_KEY);
    sessionStorage.removeItem(LMA_ACTIVE_SESSION_EXPIRES_AT_KEY);
    window.location.href = logoutRedirectUrl;
  }, [userName, logoutRedirectUrl]);

  const {
    isPrompted,
    continueSession,
    logout: handleIdleLogout,
  } = useIdleTimerHook({
    onIdle: onIdleTimeout,
    onPrompt: () => {
      // onPrompt callback - countdown will be handled by useEffect
    },
    idleTime: idleTimeoutMs,
    promptTime: idlePromptTimeoutMs,
    enabled: shouldEnableIdleTimer,
  });

  useEffect(() => {
    if (!isPrompted) {
      setRemainingSeconds(undefined);
      return;
    }

    const promptTime = idlePromptTimeoutMs;
    const startTime = Date.now();

    const countdownInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, Math.floor((promptTime - elapsed) / 1000));
      setRemainingSeconds(remaining);

      if (remaining <= 0) {
        clearInterval(countdownInterval);
        setRemainingSeconds(undefined);
      }
    }, 1000);

    return () => {
      clearInterval(countdownInterval);
    };
  }, [isPrompted, idlePromptTimeoutMs]);

  const handleContinue = () => {
    continueSession();
    setRemainingSeconds(undefined);
  };

  const handleLogout = () => {
    handleIdleLogout();
    setRemainingSeconds(undefined);
  };

  // --- RENDER LOGIC (all hooks are called above this point) ---

  const LoadingScreen = (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
      }}
    >
      <Typography variant="h6" color="text.secondary">
        Loading...
      </Typography>
    </Box>
  );

  if (!mounted) {
    return LoadingScreen;
  }

  if (isLoading) {
    return LoadingScreen;
  }

  if (!isAuthenticated && !isTerminalLmaPage) {
    return <LMAUnauthorizedView fields={accessDeniedFields} />;
  }

  const idleDialog = (
    <IdleTimeoutDialog
      open={isPrompted}
      onContinue={handleContinue}
      onLogout={handleLogout}
      remainingTime={remainingSeconds}
      fields={lmaSiteSettings}
    />
  );

  if (isSuperAdmin || maxConcurrentUsers <= 0 || isTerminalLmaPage) {
    return (
      <>
        {children}
        {isTerminalLmaPage ? null : idleDialog}
      </>
    );
  }

  return (
    <LMAConcurrentSessionGate
      maxConcurrentUsers={maxConcurrentUsers}
      userSessionTtlSeconds={userSessionTtlSeconds}
      accessDeniedFields={accessDeniedFields}
    >
      {children}
      {idleDialog}
    </LMAConcurrentSessionGate>
  );
}
